/**
 * DirectVsFundsCard.jsx — Donut chart showing Direct (Equity + Gold)
 * vs Funds (MF / Index Funds) split.
 *
 * Uses same Recharts pattern as SectorAllocationCard.
 */

import React, { useState, useCallback } from 'react';
import {
    PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Sector,
} from 'recharts';

/* ── helpers ── */
const fmt = (n, d = 2) => {
    if (n == null || isNaN(n)) return 'N/A';
    return Number(n).toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d });
};
const fmtCurrency = (n) => (n == null || isNaN(n) ? 'N/A' : `₹${fmt(n)}`);
const fmtPct = (n) => (n == null || isNaN(n) ? 'N/A' : `${fmt(n)}%`);

const RADIAN = Math.PI / 180;

const DVF_COLORS = ['#7c3aed', '#3b82f6']; // Direct = purple, Funds = blue

/* ── Outer label ── */
const renderLabel = ({ cx, cy, midAngle, outerRadius, name, percent }) => {
    if (percent < 0.01) return null;
    const radius = outerRadius + 28;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
        <text x={x} y={y} fill="var(--text-2)" textAnchor={x > cx ? 'start' : 'end'}
            dominantBaseline="central" fontSize={12} fontWeight={500}>
            {name} ({(percent * 100).toFixed(1)}%)
        </text>
    );
};

/* ── Active shape ── */
const renderActiveShape = (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent } = props;
    return (
        <g>
            <text x={cx} y={cy - 8} textAnchor="middle" fill="var(--text-1)" fontSize={15} fontWeight={600}>
                {payload.name}
            </text>
            <text x={cx} y={cy + 14} textAnchor="middle" fill="var(--text-2)" fontSize={12}>
                {(percent * 100).toFixed(1)}%
            </text>
            <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius + 8}
                startAngle={startAngle} endAngle={endAngle} fill={fill} />
            <Sector cx={cx} cy={cy} innerRadius={outerRadius + 12} outerRadius={outerRadius + 16}
                startAngle={startAngle} endAngle={endAngle} fill={fill} />
        </g>
    );
};

/* ── Tooltip ── */
const ChartTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
        <div className="sector-tooltip">
            <div className="sector-tooltip-header">
                <span className="sector-tooltip-dot" style={{ background: d.fill }} />
                <strong>{d.name}</strong>
            </div>
            <div className="sector-tooltip-row">
                <span>Value</span><span>{fmtCurrency(d.value)}</span>
            </div>
            <div className="sector-tooltip-row">
                <span>Weight</span><span>{fmtPct(d.pct)}</span>
            </div>
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */
const DirectVsFundsCard = ({ data }) => {
    const [activeIndex, setActiveIndex] = useState(-1);
    const onEnter = useCallback((_, i) => setActiveIndex(i), []);
    const onLeave = useCallback(() => setActiveIndex(-1), []);

    const items = data?.directVsFunds ?? [];
    const totalValue = data?.totalMarketValue ?? items.reduce((s, d) => s + (d.marketValue || d.value || 0), 0);

    if (!items.length || items.every((d) => !(d.marketValue || d.value))) {
        return (
            <section className="portfolio-section">
                <h3 className="portfolio-section-title">Direct vs Funds</h3>
                <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
                    No data available.
                </p>
            </section>
        );
    }

    const chartData = items.map((d, i) => ({
        name: d.label,
        value: d.marketValue || d.value || 0,
        pct: d.weightPct ?? d.pct,
        fill: DVF_COLORS[i % DVF_COLORS.length],
    }));

    return (
        <section className="portfolio-section direct-vs-funds-card">
            <h3 className="portfolio-section-title">Direct vs Funds</h3>
            <p className="sector-subtitle">Direct holdings (Equity + Gold) versus managed funds</p>

            <div className="sector-chart-container">
                <div className="sector-pie-wrap">
                    <ResponsiveContainer width="100%" height={480}>
                        <PieChart>
                            <Pie
                                data={chartData}
                                cx="50%"
                                cy="50%"
                                innerRadius={65}
                                outerRadius={120}
                                dataKey="value"
                                nameKey="name"
                                minAngle={3}
                                label={renderLabel}
                                labelLine={{ stroke: 'var(--text-3)', strokeWidth: 1 }}
                                activeIndex={activeIndex}
                                activeShape={renderActiveShape}
                                onMouseEnter={onEnter}
                                onMouseLeave={onLeave}
                                animationDuration={600}
                            >
                                {chartData.map((entry, i) => (
                                    <Cell key={entry.name} fill={entry.fill}
                                        stroke="var(--bg-card)" strokeWidth={2} />
                                ))}
                            </Pie>
                            <Tooltip content={<ChartTooltip />} />
                        </PieChart>
                    </ResponsiveContainer>
                    <div className="sector-center-label">
                        <div className="sector-center-value">{fmtCurrency(totalValue)}</div>
                        <div className="sector-center-sub">Total Value</div>
                    </div>
                </div>

                {/* Legend */}
                <div className="sector-legend-wrap">
                    <ul className="sector-legend">
                        {chartData.map((entry) => (
                            <li key={entry.name} className="sector-legend-item">
                                <span className="sector-legend-dot" style={{ background: entry.fill }} />
                                <span className="sector-legend-label">{entry.name}</span>
                                <span className="sector-legend-pct">{fmtPct(entry.pct)}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    );
};

export default DirectVsFundsCard;
